import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { Phone, Clock, MapPin, ShieldCheck } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

/**
 * Mission section — placed just under the hero to reinforce brand values and
 * dispatch-readiness with the copy provided by the owner.
 */
export default function Mission() {
  const leftRef = useReveal();
  const rightRef = useReveal(0.05);
  return (
    <section
      id="mission"
      data-testid="mission-section"
      className="relative bg-[#0A0A0A] py-24 sm:py-32 border-t border-white/5 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at 90% 10%, rgba(250,204,21,0.14) 0%, rgba(0,0,0,0) 55%)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-start">
        {/* Left rail — big overlapping call-out */}
        <div ref={leftRef} className="lux-reveal lg:col-span-5 relative">
          <div
            className="inline-flex items-center gap-2 lux-glass rounded-full px-3 py-1.5"
            data-testid="mission-eyebrow"
          >
            <Phone className="h-3.5 w-3.5 text-yellow-400" strokeWidth={3} aria-hidden="true" />
            <span className="font-display uppercase tracking-[0.25em] text-[10px] text-white/90">
              Call 24 Hours a Day
            </span>
          </div>

          <div className="mt-4 font-display uppercase tracking-[0.32em] text-yellow-400 text-xs">
            Proudly Serving the Eastside
          </div>

          <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
            Our <span className="text-yellow-400">mission</span>
            <span className="block">— serve the customer first.</span>
          </h2>

          <div className="mt-8 space-y-3">
            {[
              { icon: Clock, label: "Answering the phone 24/7 — real people" },
              { icon: MapPin, label: "Clean, maintained trucks + flatbed carriers" },
              { icon: ShieldCheck, label: "Trained, professional, understanding drivers" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 text-zinc-300 text-sm sm:text-base font-light">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                  <item.icon className="h-4 w-4 text-yellow-400" strokeWidth={2.2} aria-hidden="true" />
                </div>
                {item.label}
              </div>
            ))}
          </div>

          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="mission-call-btn"
            aria-label={`Call BM Towing & Recovery 24/7 at ${PHONE_DISPLAY}`}
            className="mt-10 inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-4 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} aria-hidden="true" /> Call {PHONE_DISPLAY}
          </a>
        </div>

        {/* Right — mission copy inside a glass card */}
        <div ref={rightRef} className="lux-reveal lux-reveal-delay-2 lg:col-span-7">
          <div className="relative lux-glass-strong rounded-2xl p-8 sm:p-10 overflow-hidden">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-30"
              style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 70%)" }}
            />
            <span
              aria-hidden="true"
              className="relative font-display font-black text-yellow-400/20 text-8xl leading-none tracking-tighter"
            >
              &ldquo;
            </span>
            <p
              className="relative -mt-6 sm:-mt-8 font-display text-white text-lg sm:text-xl lg:text-2xl leading-relaxed font-light"
              data-testid="mission-body"
            >
              <span className="font-display font-black uppercase text-yellow-400 tracking-tight">BM Towing's mission</span>{" "}
              is to help our customers cope with the needs of everyday life and to smoothly manage life's little surprises. Serving the customer is our first intention. Whenever a customer calls, an educated, friendly BM Towing driver in a clean and maintained tow truck or flatbed car carrier is dispatched within a few minutes. The driver is trained to carefully assess any situation before acting, and behave in a professional and understanding manner.
            </p>

            <div className="relative mt-8 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
              <div className="font-display uppercase tracking-[0.28em] text-[11px] text-zinc-400">
                Contact us now
              </div>
              <a
                href="#contact"
                data-testid="mission-contact-btn"
                className="inline-flex items-center gap-2 lux-glass hover:border-yellow-400/50 text-white font-display font-bold uppercase tracking-wider px-5 py-3 rounded-full transition-colors"
              >
                Request Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
